import PropTypes from 'prop-types'

import {useSuiContext} from '@s-ui/react-context'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import IconCheckSmall from '@adv-mt/mt-svg-icons/lib/CheckSmall.js'
import IconTriangleWarningOutline from '@adv-mt/mt-svg-icons/lib/TriangleWarningOutline.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {Button, BUTTON_SHAPES, BUTTON_SIZES, BUTTON_VARIANTS} from '@adv-mt/ui/button'
import {Icon, ICON_INTENTS, ICON_SIZES} from '@adv-mt/ui/icon'
import {Body1, Caption, Subhead1} from '@adv-mt/ui/typography'

import {BASE_CLASS as PAGE_BASE_CLASS} from '../../../config.js'
import {DRAWER_TYPES} from '../../../drawers/config.js'

const BASE_CLASS = `${PAGE_BASE_CLASS}-carfax`

const ITEM_ICONS = {
  ok: {Icon: IconCheckSmall, intent: ICON_INTENTS.PRIMARY},
  warning: {Icon: IconTriangleWarningOutline, intent: ICON_INTENTS.ALERT}
}

// Trust only ever mounts this component when vehicleHistory.hasReport is true — the no-report state lives in the Advantages panel
// This is when dealer has paid for the carfax report and you can see it on a drawer
const Carfax = ({adId, onOpenDrawer, vehicleHistory}) => {
  const {domain, i18n} = useSuiContext()
  const {track: analyticsTrack} = useAnalyticsTrack()

  const config = domain.get('config')
  const {TRACKING_EVENT_NAMES} = config.get('tracking')

  const isHigherThanMobile = useMediaQuery(useMediaQueryBreakpoints.sm, {
    defaultMatches: !domain.get('config').get('isMobile')
  })

  const {reportUrl, verifiedData} = vehicleHistory
  const items = verifiedData?.value ?? []

  const handleShowReport = () => {
    onOpenDrawer({data: reportUrl, type: DRAWER_TYPES.CARFAX})
    domain
      .get('get_advertisements_tracking_use_case')
      .execute({ads: [adId], context: 'other'})
      .then(({products}) =>
        analyticsTrack({data: {products}, event: TRACKING_EVENT_NAMES.FREE_VEHICLE_HISTORY_CLICKED})
      )
  }

  return (
    <div className={BASE_CLASS}>
      <div className={`${BASE_CLASS}-header`}>
        <Subhead1 as="h3">{i18n.t('CARFAX.TRUST_PANEL.TITLE')}</Subhead1>
      </div>

      <div className={`${BASE_CLASS}-logo`}>
        <img
          alt="CARFAX"
          src={`${domain.get('config').get('URL_STATICS_IMAGES')}common/logos/carfax-logo-white-box.svg`}
        />
      </div>

      <ul className={`${BASE_CLASS}-list`}>
        {items.map(({text, value}, index) => {
          const {Icon: ItemIcon, intent} = ITEM_ICONS[value] ?? ITEM_ICONS.ok
          return (
            <li className={`${BASE_CLASS}-item`} key={index}>
              <Icon size={ICON_SIZES.MD} intent={intent}>
                <ItemIcon size={ICON_SIZES.MD} />
              </Icon>
              <Body1>{text}</Body1>
            </li>
          )
        })}
      </ul>

      <Caption className={`${BASE_CLASS}-legal`}>{i18n.t('CARFAX.TRUST_PANEL.LEGAL_TEXT')}</Caption>

      <div className={`${BASE_CLASS}-actions`}>
        <Button
          onClick={handleShowReport}
          shape={BUTTON_SHAPES.CIRCLE}
          size={isHigherThanMobile ? BUTTON_SIZES.MEDIUM : BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.OUTLINE}
        >
          {i18n.t('CARFAX.TRUST_PANEL.SHOW_REPORT')}
        </Button>
      </div>
    </div>
  )
}

Carfax.displayName = 'Carfax'

Carfax.propTypes = {
  adId: PropTypes.string.isRequired,
  onOpenDrawer: PropTypes.func.isRequired,
  vehicleHistory: PropTypes.shape({
    reportUrl: PropTypes.string,
    verifiedData: PropTypes.shape({
      value: PropTypes.arrayOf(PropTypes.shape({text: PropTypes.string, value: PropTypes.string}))
    })
  }).isRequired
}

export default Carfax
